// src/lib/managers/mail/MailActions.ts

import { EventBus } from "../../contexts";
import { isNotFoundError, logger, LOGS } from "../../utils";
import { ApiManager } from "../api/ApiManager";
import { CO2Manager } from "../co2";
import { CustomViewManager } from "../customView";
import { DataManager } from "../dataManager/DataManager";
import { MailFolder } from "../dataManager/graph/types/graph";
import { BatchDeleteItem } from "../dataManager/IDataApi";
import { UserTodos } from "../user/UserTodos";
import {
    MailDeletionResult,
    parseUniqueMailvistaId,
    uniqueMailvistaId,
} from "./domain/mailIdentity";
import { MailRecord } from "./MailRecord";
import { MailSync } from "./MailSync";
import { BulkActionResult, BulkMailAction } from "./types/bulkMailAction";
import {
    LinkedEventDetails,
    Mail,
    MailSend,
    ReplyForward,
    SmartLabel,
} from "./types/mail";
import { ForwardMailRequestBody } from "./types/mail.api";

export class MailActions {
    private eventBus: EventBus;

    private mailRecord: MailRecord;

    private mailSync: MailSync;

    private apiManager: ApiManager;

    private dataManager: DataManager;

    private userTodos: UserTodos;

    private customViewManager: CustomViewManager;

    private CO2Manager: CO2Manager;

    constructor(
        eventBus: EventBus,
        mailRecord: MailRecord,
        mailSync: MailSync,
        apiManager: ApiManager,
        dataManager: DataManager,
        userTodos: UserTodos,
        customViewManager: CustomViewManager,
        CO2Manager: CO2Manager,
    ) {
        this.eventBus = eventBus;
        this.mailRecord = mailRecord;
        this.mailSync = mailSync;
        this.apiManager = apiManager;
        this.dataManager = dataManager;
        this.userTodos = userTodos;
        this.customViewManager = customViewManager;
        this.CO2Manager = CO2Manager;
    }

    public async setMailFocus(
        uniqueMailvistaId: string,
        isFocus: boolean,
    ): Promise<boolean> {
        logger.info(
            LOGS.MailManager,
            `setMailFocus [${uniqueMailvistaId}] isFocus=${isFocus}`,
        );

        try {
            // WS6b : signature copie-aware. Focus = op SmartLabel (canonique) → le Record cible la copie inbox
            // (parse→imid→_getCanonicalMail) ; l'uid est forwardé tel quel à l'API (le Backend en dérive l'imid).
            const { internetMessageId } =
                parseUniqueMailvistaId(uniqueMailvistaId);
            const mail = this.mailSync._getCanonicalMail(internetMessageId);
            const updatedMail = await this.apiManager.setMailFocus(
                uniqueMailvistaId,
                isFocus,
            );

            if (updatedMail && this.mailRecord.byInternetMessageId && mail) {
                mail.smartLabel = updatedMail.smartLabel;
                mail.isUserEdited = updatedMail.isUserEdited;
                mail.isUserProcessed = updatedMail.isUserProcessed;
                mail.isFocus = updatedMail.isFocus;
                mail.manualFocusDate = updatedMail.manualFocusDate ?? null;
                // Le backend réécrit les catégories IA lors du toggle focus → on rafraîchit la copie locale
                // pour que le chip catégorie reflète le nouvel état sans re-fetch.
                if (updatedMail.categories && mail.aiDetails) {
                    mail.aiDetails.categories = updatedMail.categories;
                }
                this.eventBus.emit("mail-updated", mail);
            } else {
                logger.warn(
                    [LOGS.MailManager],
                    `setMailFocus - cannot update Record => should not happen`,
                );
            }

            // Renvoie true si updatedMail contient quelque chose
            return !!updatedMail;
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : String(error);
            const errorText = `Error setting mail focus state: ${errorMessage}`;
            logger.error(LOGS.MailManager, errorText);
            throw new Error(errorText);
        }
    }

    public async updateMailSmartLabel(
        uniqueMailvistaId: string,
        smartLabel: SmartLabel,
    ): Promise<boolean> {
        logger.info(
            LOGS.MailManager,
            `updateMailSmartLabel for mail ${uniqueMailvistaId} to ${smartLabel}`,
        );

        try {
            // WS6b (décision 3B) : signature copie-aware. SmartLabel = op canonique → le Record cible la copie
            // inbox (parse→imid→_getCanonicalMail) ; l'uid est forwardé à l'API (le Backend en dérive l'imid).
            const { internetMessageId } =
                parseUniqueMailvistaId(uniqueMailvistaId);
            const mail = this.mailSync._getCanonicalMail(internetMessageId);

            const updatedMail = await this.apiManager.setMailSmartLabel(
                uniqueMailvistaId,
                smartLabel,
            );

            // Mise à jour du Record local avec les champs retournés par le backend
            // (les règles métier peuvent avoir modifié smartLabel, isUserEdited, isUserProcessed ou isFocus)
            // _updateSmartLabelEntryAndSendEvent dans CustomViewManager gère le reste (store, navigation, contenu)
            if (updatedMail && this.mailRecord.byInternetMessageId && mail) {
                mail.smartLabel = updatedMail.smartLabel;
                mail.isUserEdited = updatedMail.isUserEdited;
                mail.isUserProcessed = updatedMail.isUserProcessed;
                mail.isFocus = updatedMail.isFocus;

                this.eventBus.emit("mail-updated", mail);
            } else {
                logger.warn(
                    [LOGS.MailManager],
                    `updateMailSmartLabel - cannot update Record => should not happen`,
                );
            }

            // Renvoie true si updatedMail contient quelque chose
            return !!updatedMail;
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : String(error);
            const errorText = `Error updating mail smartLabel: ${errorMessage}`;
            logger.error(LOGS.MailManager, errorText);
            throw new Error(errorText);
        }
    }

    public async setMailProcessed(
        uniqueMailvistaId: string,
        isUserProcessed: boolean,
    ): Promise<boolean> {
        logger.info(
            LOGS.MailManager,
            `setMailProcessed to ${uniqueMailvistaId} to ${isUserProcessed}`,
        );

        try {
            // WS6b : signature copie-aware. Processed = op SmartLabel (canonique) → le Record cible la copie inbox
            // (parse→imid→_getCanonicalMail) ; l'uid est forwardé tel quel à l'API (le Backend en dérive l'imid).
            const { internetMessageId } =
                parseUniqueMailvistaId(uniqueMailvistaId);
            const mail = this.mailSync._getCanonicalMail(internetMessageId);
            const updatedMail = await this.apiManager.setMailProcessed(
                uniqueMailvistaId,
                isUserProcessed,
            );

            if (updatedMail && this.mailRecord.byInternetMessageId && mail) {
                mail.smartLabel = updatedMail.smartLabel;
                mail.isUserEdited = updatedMail.isUserEdited;
                mail.isUserProcessed = updatedMail.isUserProcessed;
                mail.isFocus = updatedMail.isFocus;

                if (isUserProcessed) {
                    mail.isRead = true;
                }

                this.eventBus.emit("mail-updated", mail);
            } else {
                logger.warn(
                    [LOGS.MailManager],
                    `setMailProcessed - cannot update Record => should not happen`,
                );
            }

            // Renvoie true si updatedMail contient quelque chose
            return !!updatedMail;
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : String(error);
            const errorText = `Error setting mail processed state: ${errorMessage}`;
            logger.error(LOGS.MailManager, errorText);
            throw new Error(errorText);
        }
    }

    /**
     * @method setImportance
     * Définit l'importance d'un email
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {string} messageId - Identifiant du message
     * @input {'low' | 'normal' | 'high'} importance - Niveau d'importance
     * @output {Promise<boolean>} Succès de l'opération
     * @throws {Error} Si GraphManager n'est pas initialisé
     */
    // public setImportance(messageId: string, importance: 'low' | 'normal' | 'high'): Promise<boolean> {

    //   if (!this.graphManager) {
    //     throw new Error('GraphManager is not set');
    //   }

    //   return this.graphManager.setImportance(messageId, importance);
    // }

    /**
     * @method setFlag
     * Définit le statut de marquage d'un email
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {string} messageId - Identifiant du message
     * @input {'notFlagged' | 'complete' | 'flagged'} flagStatus - Statut du marquage
     * @output {Promise<boolean>} Succès de l'opération
     * @throws {Error} Si GraphManager n'est pas initialisé
     */
    // public setFlag(messageId: string, flagStatus: 'notFlagged' | 'complete' | 'flagged'): Promise<boolean> {

    //   if (!this.graphManager) {
    //     throw new Error('GraphManager is not set');
    //   }

    //   return this.graphManager.setFlag(messageId, flagStatus);
    // }

    public async setReadStatus(
        uniqueMailvistaId: string,
        isRead: boolean,
    ): Promise<boolean> {
        logger.info(
            [LOGS.MailManager],
            `setReadStatus to ${uniqueMailvistaId} to  ${isRead}`,
        );

        try {
            // WS6b : read = état PAR COPIE (Graph). On cible la copie EXACTE (parse→_getCopy), pas le canonique.
            const { internetMessageId, parentFolderId } =
                parseUniqueMailvistaId(uniqueMailvistaId);
            const mail = this.mailSync._getCopy(
                internetMessageId,
                parentFolderId,
            );
            const mailId = mail?.id;
            if (!mail || !mailId) {
                logger.error(
                    [LOGS.MailManager],
                    `Copie ${uniqueMailvistaId} introuvable dans mailsRecord`,
                );
                return Promise.resolve(false);
            }

            // Garde-fou : si le mail est déjà dans l'état cible, ne rien faire.
            // Évite un PATCH Graph inutile + l'émission de mail-updated qui déclenche
            // toute la cascade (réinsertion SmartLabel → saut d'ordre, refetch liste,
            // _verifyExistingMailsForView → appels réseau, recompute navbar).
            if (mail.isRead === isRead) {
                logger.info(
                    [LOGS.MailManager],
                    `setReadStatus - mail ${internetMessageId} déjà à isRead=${isRead}, no-op`,
                );
                return true;
            }

            const result: boolean = await this.dataManager.setReadStatus(
                mailId,
                isRead,
            );

            // Update local record if operation was successfull
            if (result && mail) {
                mail.isRead = isRead;

                // Update Custom Views with updated mail
                // Should be process by CustomViewManager on mail-updated event
                // await this.customViewManager._updateAllCustomViewsIfNeededAndSendEvent(mail);

                this.eventBus.emit("mail-updated", mail);
                this.eventBus.emit("navigation-asking-for-update");
            } else {
                logger.warn(
                    [LOGS.MailManager],
                    `setReadStatus - cannot update Record => should not happen !!!`,
                );
            }

            return result;
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : String(error);
            const errorText = `Error setting mail read status: ${errorMessage}`;
            logger.error(LOGS.MailManager, errorText);
            throw new Error(errorText);
        }
    }

    /**
     * Marks a list of mail COPIES as read or unread via a single Graph $batch request,
     * avoiding the 429 (Too Many Requests) errors caused by parallel individual PATCH calls.
     *
     * #1728 / WS6b — le read est un état PAR COPIE (Graph) et peut cibler TOUTE la boîte : on agit
     * sur la copie EXACTE (`parentFolderId` de la sélection), pas sur la copie inbox canonique.
     *
     * Translation layer: the Graph API requires the internal `id`. This method maps each copy's
     * Graph id → back to its `uniqueMailvistaId` in the returned result.
     *
     * Local Record and events are updated only for successfully processed copies.
     *
     * @param copies  - exact mail copies (Mail) to update
     * @param isRead  - Target read state (true = read, false = unread)
     * @returns success/failed lists expressed in uniqueMailvistaId space
     */
    private async _batchSetReadStatus(
        copies: Mail[],
        isRead: boolean,
    ): Promise<{ success: string[]; failed: string[] }> {
        const failed: string[] = [];

        // Build Graph-API items from the exact copies (skip copies without a Graph id → failed).
        const graphItems: { messageId: string; isRead: boolean }[] = [];
        // Reverse map: Graph ID → exact copy (needed to translate results back + update the Record).
        const graphIdToCopy = new Map<string, Mail>();

        for (const copy of copies) {
            if (!copy?.id) {
                logger.error(
                    [LOGS.MailManager],
                    `_batchSetReadStatus: copie sans id (${copy?.internetMessageId}) absente du Record`,
                );
                if (copy) failed.push(uniqueMailvistaId(copy));
                continue;
            }
            graphItems.push({ messageId: copy.id, isRead });
            graphIdToCopy.set(copy.id, copy);
        }

        const batchResult =
            await this.dataManager.batchSetReadStatus(graphItems);

        const success: string[] = [];

        // Apply local state update and per-mail event for each success (copie EXACTE)
        for (const graphId of batchResult.success) {
            const copy = graphIdToCopy.get(graphId);
            if (!copy) continue;
            copy.isRead = isRead;
            this.eventBus.emit("mail-updated", copy);
            success.push(uniqueMailvistaId(copy));
        }

        // Graph-level failures → uniqueMailvistaId space
        for (const graphId of batchResult.failed) {
            const copy = graphIdToCopy.get(graphId);
            failed.push(copy ? uniqueMailvistaId(copy) : graphId);
        }

        // Single navigation refresh for the whole batch (avoids N redundant re-renders)
        if (success.length > 0) {
            this.eventBus.emit("navigation-asking-for-update");
        }

        return { success, failed };
    }

    public async sendMail(mailData: MailSend): Promise<boolean> {
        logger.info(
            [LOGS.MailManager],
            `sendMail to ${mailData.toRecipients.join(", ")}...`,
        );

        const success = await this.dataManager.sendMail(mailData);

        if (success) {
            logger.info([LOGS.MailManager], `Mail sent successfully`);
        } else {
            logger.error([LOGS.MailManager], `Failed to send mail`);
        }

        return success;
    }

    public async replyToMail(
        internetMessageId: string,
        replyData: ReplyForward,
        replyAll: boolean = false,
    ): Promise<boolean> {
        logger.info(
            [LOGS.MailManager],
            `replyToMail to ${replyData.toRecipients?.join(", ")}...`,
        );

        const mailId = this.mailSync._getCanonicalMail(internetMessageId)?.id;
        if (!mailId) {
            logger.error(
                [LOGS.MailManager],
                `Mail with internetMessageId ${internetMessageId} not found in mailsRecord`,
            );
            return Promise.resolve(false);
        }

        return this.dataManager.replyToMail(mailId, replyData, replyAll);
    }

    public async forwardMail(
        payload: ForwardMailRequestBody,
    ): Promise<boolean> {
        logger.info(
            [LOGS.MailManager],
            `forwardMail to ${payload.toRecipients.join(", ")}...`,
        );

        let sentInternetMessageId: string;
        try {
            ({ internetMessageId: sentInternetMessageId } =
                await this.apiManager.forwardMail(payload));
        } catch (error) {
            logger.error(
                [LOGS.MailManager],
                `forwardMail failed for ${payload.forwardId}`,
                error,
            );

            return false;
        }

        if (sentInternetMessageId) {
            await this.dataManager
                .emitMailSentByInternetMessageId(sentInternetMessageId)
                .catch((error) =>
                    logger.warn(
                        [LOGS.MailManager],
                        `Émission de 'mail-sent' impossible après le transfert ${payload.forwardId}: ${error}`,
                    ),
                );
        }

        return true;
    }

    /**
     * Applies all local side effects for a mail that has just been moved to the Deleted Items
     * folder (i.e. first-level delete, not a permanent purge).
     *
     * Two distinct operations:
     * 1. DB cleanup — only for mails originating from Inbox. Mails from other folders (Sent,
     *    Junk, etc.) are not tracked in our DB, so attempting to delete them is a no-op.
     *    The try/catch is intentional: absence from DB is a normal case for unanalysed mails.
     * 2. Record reset via `_updateRecordWithoutAnalysis` — updates the Graph ID (the moved mail
     *    gets a new ID), moves parentFolderId to deletedItems, and clears AI/SmartLabel fields.
     *    `mail-deleted` is then emitted so CustomViewManager removes the mail from all views.
     *
     * @param internetId    - MailVista internetMessageId of the moved mail
     * @param newMessageId  - New Graph message ID assigned by Graph after the move
     * @param sourceFolderId - WS5d : dossier d'origine de la copie déplacée (re-key copie-précis) ;
     *                         si absent, fallback canonique (compatibilité).
     */
    private async _applyMoveToTrashSideEffect(
        internetId: string,
        newMessageId: string,
        sourceFolderId?: string,
    ): Promise<void> {
        const mail =
            sourceFolderId !== undefined
                ? this.mailSync._getCopy(internetId, sourceFolderId)
                : this.mailSync._getCanonicalMail(internetId);
        // WS5c (B) : uid de la copie d'origine, capturé AVANT la mutation parentFolderId.
        const deletedCopyUid = mail ? uniqueMailvistaId(mail) : undefined;
        // DB entry exists only for mails that have been analysed and were in Inbox
        if (mail?.parentFolderId === this.mailRecord.inboxFolderId) {
            try {
                // [WS-C / AB#1576] migré Core → Api-Backend : suppression pilotée par internetMessageId
                // (plus de résolution de mailId Cosmos côté SDK).
                await this.apiManager.deleteAnalyzedMailsByInternetMessageIds([
                    internetId,
                ]);
            } catch {
                // Normal if the mail was never analysed (no DB record exists)
                logger.warn(
                    [LOGS.MailManager],
                    `_batchDeleteMails: mailId absent de la DB, normal si non analysé : ${internetId}`,
                );
            }
        }

        // Update the Record with the new Graph ID and reset AI fields (re-key copie-précis WS5d)
        this.mailSync._updateRecordWithoutAnalysis(
            newMessageId,
            internetId,
            sourceFolderId,
        );
        if (deletedCopyUid) {
            this.eventBus.emit("mail-deleted", {
                uniqueMailvistaId: deletedCopyUid,
            });
        }
    }

    /**
     * AB#1764 — Side effects pour une copie **déjà supprimée côté messagerie** (Outlook / hors
     * MailVista) : le provider a répondu 404 sur le move/delete. La suppression est **idempotente**
     * (le mail est déjà dans l'état voulu) → on nettoie le local comme une suppression réussie, mais
     * SANS re-key : la copie n'existe plus à la source, on RETIRE donc son slot du Record.
     *
     * Deux opérations (cf. `_applyMoveToTrashSideEffect`) :
     * 1. DB cleanup best-effort — seulement pour une copie venant d'Inbox (mail analysé). Le try/catch
     *    est intentionnel : l'absence en DB est un cas normal (mail non analysé).
     * 2. Retrait du slot via `_computeDeletionResult` + `applyDeletionResult`, puis `mail-deleted`
     *    (CustomViewManager retire le mail des vues / du store SmartLabel → compteurs corrigés).
     *
     * @param internetId    - internetMessageId de la copie disparue
     * @param graphId       - id Graph de la copie ciblée (résolution slot-exacte dans le Record)
     * @param sourceFolderId - dossier d'origine de la copie (détermine le cleanup DB) ; fallback = parentFolderId du Record
     */
    private async _applyAlreadyDeletedSideEffect(
        internetId: string,
        graphId: string,
        sourceFolderId?: string,
    ): Promise<void> {
        const mail = this._findCopyByGraphId(graphId);
        if (!mail) {
            // La copie a déjà quitté le Record (ne devrait pas arriver : le batch est bâti depuis le Record).
            logger.warn(
                [LOGS.MailManager],
                `_applyAlreadyDeletedSideEffect: copie graphId ${graphId} déjà absente du Record (${internetId})`,
            );
            return;
        }
        const deletedCopyUid = uniqueMailvistaId(mail);

        // DB entry exists only for mails that have been analysed and were in Inbox
        if (
            (sourceFolderId ?? mail.parentFolderId) ===
            this.mailRecord.inboxFolderId
        ) {
            try {
                await this.apiManager.deleteAnalyzedMailsByInternetMessageIds([
                    internetId,
                ]);
            } catch {
                logger.warn(
                    [LOGS.MailManager],
                    `_applyAlreadyDeletedSideEffect: mailId absent de la DB, normal si non analysé : ${internetId}`,
                );
            }
        }

        // Retrait du slot (pas de re-key : la copie n'existe plus à la source).
        const deletionResult = this._computeDeletionResult(mail, false);
        this.applyDeletionResult(deletionResult);
        this.eventBus.emit("mail-deleted", {
            uniqueMailvistaId: deletedCopyUid,
        });
    }

    /**
     * Deletes a list of mails via Graph $batch, handling two distinct branches per mail:
     *
     * • Permanent delete (`isPermanent = true`): mail is already in Deleted Items.
     *   Graph operation: `DELETE /me/messages/{id}` → 204 No Content.
     *   Side effects: removes from Record, increments CO2 counter.
     *
     * • Move to trash (`isPermanent = false`): first-level delete from any other folder.
     *   Graph operation: `POST /me/messages/{id}/move` → 201 with the new message object.
     *   The new ID must be captured because Graph reassigns the message ID on move.
     *   Side effects: delegated to `_applyMoveToTrashSideEffect` (DB + record reset + event).
     *
     * The Graph $batch call is made via `dataManager.batchDeleteMails`, which handles
     * chunking (max 20/batch), exponential backoff retry on 429/5xx, and per-item result parsing.
     *
     * WS5d — prend les **copies exactes** à supprimer (slot-precise) plutôt que des internetMessageIds
     * (qui ré-résolvaient la copie canonique). Permet de cibler la copie d'un dossier précis (Sent, Inbox…).
     *
     * @param copies - copies exactes (Mail) à supprimer
     * @returns success/failed lists in uniqueMailvistaId space (#1728 — copie-précis)
     */
    private async _batchDeleteMails(
        copies: Mail[],
    ): Promise<{ success: string[]; failed: string[] }> {
        if (!this.mailRecord.deleteditemsFolderId)
            throw new Error(
                "_batchDeleteMails - deletedItems folder ID not found",
            );

        const notFoundUniqueIds: string[] = [];
        const batchItems: (BatchDeleteItem & { internetMessageId: string })[] =
            [];
        // WS5d : dossier d'origine de chaque copie (par graphId) → re-key copie-précis au move.
        const graphIdToSourceFolderId = new Map<string, string>();
        // #1728 : graphId → uniqueMailvistaId de la copie ciblée (retour copie-précis attendu par le front).
        const graphIdToUniqueId = new Map<string, string>();

        // AB#1764 — on mémorise AVANT la suppression si au moins un mail Inbox du lot alimentait le
        //   dashboard (todos acceptés). Le backend retire ces todos en cascade (keyé internetMessageId)
        //   → un unique refresh en fin de lot suffit (cf. resync ci-dessous, parité chemin single).
        const hadDashboardTodos = copies.some(
            (copy) =>
                copy?.parentFolderId === this.mailRecord.inboxFolderId &&
                (copy.aiDetails?.toDo?.some((todo) => todo.isUserAccepted) ??
                    false),
        );

        // Build Graph-API items and classify each as permanent or move-to-trash
        for (const copy of copies) {
            if (!copy?.id) {
                logger.error(
                    [LOGS.MailManager],
                    `_batchDeleteMails: copie sans id (${copy?.internetMessageId}) absente du Record`,
                );
                if (copy?.internetMessageId)
                    notFoundUniqueIds.push(uniqueMailvistaId(copy));
                continue;
            }
            batchItems.push({
                internetMessageId: copy.internetMessageId,
                messageId: copy.id,
                // Copie déjà en Deleted Items → purge permanente ; sinon → move to trash
                isPermanent:
                    copy.parentFolderId ===
                    this.mailRecord.deleteditemsFolderId,
            });
            graphIdToSourceFolderId.set(copy.id, copy.parentFolderId);
            graphIdToUniqueId.set(copy.id, uniqueMailvistaId(copy));
        }

        // Reverse map: Graph ID → internetMessageId (needed to translate results back)
        const graphIdToInternetId = new Map<string, string>(
            batchItems.map((item) => [item.messageId, item.internetMessageId]),
        );

        const batchResult = await this.dataManager.batchDeleteMails(
            batchItems.map(({ messageId, isPermanent }) => ({
                messageId,
                isPermanent,
            })),
        );

        // Mails not found in Record are definitive failures (no Graph call was made for them)
        const success: string[] = [];
        const failed: string[] = [...notFoundUniqueIds];

        // Permanent deletes: remove from Record and track CO2
        for (const graphId of batchResult.permanentDeleted) {
            const internetId = graphIdToInternetId.get(graphId);
            if (!internetId) continue;
            // WS5c-3 : suppression SLOT-EXACTE — la copie purgée est celle ciblée par le batch (en Deleted
            //   Items), identifiée par son graphId. On ne retire QUE ce slot (plus de `delete record[imid]`
            //   message-entier qui évinçait à tort une copie Sent survivante). Émission APRÈS le retrait (WS5c-2).
            const deletedCopy = this._findCopyByGraphId(graphId);
            if (deletedCopy) {
                const deletionResult = this._computeDeletionResult(
                    deletedCopy,
                    false,
                );
                this.applyDeletionResult(deletionResult);
                this.eventBus.emit("mail-deleted", {
                    uniqueMailvistaId:
                        deletionResult.deletedCopy.uniqueMailvistaId,
                });
            } else {
                // graphId introuvable dans le Record (ne devrait pas arriver : le batch a été bâti depuis le Record).
                logger.error(
                    [LOGS.MailManager],
                    `_batchDeleteMails: copie graphId ${graphId} introuvable dans le Record pour la purge permanente (${internetId})`,
                );
            }
            this.CO2Manager.updateCO2OnDeleteMail();
            success.push(graphIdToUniqueId.get(graphId) ?? internetId);
        }

        // Moves to trash: delegate all side effects (DB, record reset, event)
        for (const { messageId, newMessageId } of batchResult.moved) {
            const internetId = graphIdToInternetId.get(messageId);
            if (!internetId) continue;
            // WS5d : re-key copie-précis du slot d'origine (dossier de la copie déplacée).
            const sourceFolderId = graphIdToSourceFolderId.get(messageId);
            await this._applyMoveToTrashSideEffect(
                internetId,
                newMessageId,
                sourceFolderId,
            );
            success.push(graphIdToUniqueId.get(messageId) ?? internetId);
        }

        // AB#1764 — copies déjà supprimées côté messagerie (404) → suppression idempotente (succès no-op).
        //   La copie n'existe plus à la source : retrait du slot + DB best-effort si inbox + event.
        for (const graphId of batchResult.notFound) {
            const internetId = graphIdToInternetId.get(graphId);
            if (!internetId) continue;
            const sourceFolderId = graphIdToSourceFolderId.get(graphId);
            await this._applyAlreadyDeletedSideEffect(
                internetId,
                graphId,
                sourceFolderId,
            );
            // #1728 : émettre l'uid comme les branches permanentDeleted/moved/failed (contrat uniforme
            //   uniqueMailvistaId + évite le doublon multi-copies : 2 copies-sœurs qui 404 donnaient 2× l'internetMessageId brut).
            success.push(graphIdToUniqueId.get(graphId) ?? internetId);
        }

        // Graph-level failures (exhausted retries or non-retryable error)
        for (const graphId of batchResult.failed) {
            const internetId = graphIdToInternetId.get(graphId) ?? graphId;
            logger.error(
                [LOGS.MailManager],
                `_batchDeleteMails: ${internetId} failed`,
            );
            failed.push(graphIdToUniqueId.get(graphId) ?? internetId);
        }

        // Single navigation refresh for the whole batch
        if (success.length > 0) {
            this.eventBus.emit("navigation-asking-for-update");
        }

        // AB#1764 — resync des todos dashboard (parité avec le chemin single `deleteMail`). Si l'un des
        //   mails supprimés alimentait le dashboard, le backend a retiré ses todos en cascade
        //   (deleteAnalyzedMailsByInternetMessageIds) → un seul refresh + emit pour tout le lot.
        if (success.length > 0 && hadDashboardTodos) {
            try {
                const userTodos = await this.userTodos.getUserTodos(true);
                this.eventBus.emit(
                    "user-todos-updated",
                    userTodos.filter((todo) => !todo.done),
                );
            } catch (todoError) {
                logger.warn(
                    [LOGS.MailManager],
                    `_batchDeleteMails - échec du refresh des todos après suppression bulk: ${todoError}`,
                );
            }
        }

        return { success, failed };
    }

    public async deleteMail(uniqueMailvistaId: string): Promise<boolean> {
        const { internetMessageId, parentFolderId } =
            parseUniqueMailvistaId(uniqueMailvistaId);
        logger.info([LOGS.MailManager], `Deleting Mail ${uniqueMailvistaId}`);

        try {
            // Getting the EXACT copy targeted by the uniqueMailvistaId (slot-precise, WS5d)
            const mailToDelete = this.mailSync._getCopy(
                internetMessageId,
                parentFolderId,
            );

            // Checking consistency
            if (!mailToDelete) {
                logger.error(
                    [LOGS.MailManager],
                    `Could not find the copy associated to uniqueMailvistaId ${uniqueMailvistaId} in Record`,
                );
                throw new Error(
                    `Could not find the copy associated to uniqueMailvistaId ${uniqueMailvistaId} in Record`,
                );
            }

            if (!this.mailRecord.deleteditemsFolderId) {
                logger.error(
                    [LOGS.MailManager],
                    `Unable to complete operation: deletedItems folder ID not found or unavailable`,
                );
                throw new Error(
                    "Unable to complete operation: deletedItems folder ID not found or unavailable",
                );
            }

            // Case permanent delete
            if (
                mailToDelete.parentFolderId ===
                this.mailRecord.deleteditemsFolderId
            ) {
                logger.info(
                    [LOGS.MailManager],
                    `Deleting Mail from Graph  : ${mailToDelete.id}`,
                );

                // AB#1764 — suppression idempotente : si le mail a déjà été supprimé côté messagerie
                //   (Outlook / hors MailVista), le DELETE renvoie 404. On traite ce cas comme un no-op
                //   réussi (le mail est déjà dans l'état voulu) et on nettoie le local comme d'habitude.
                let alreadyGone = false;
                try {
                    await this.dataManager.deleteMail(mailToDelete.id);
                    logger.info([LOGS.MailManager], `  => Graph Delete done`);
                } catch (error) {
                    if (!isNotFoundError(error)) throw error;
                    logger.warn(
                        [LOGS.MailManager],
                        `deleteMail - copie déjà supprimée côté messagerie (404), suppression idempotente : ${uniqueMailvistaId}`,
                    );
                    alreadyGone = true;
                }

                // if (this.mailRecord.byInternetMessageId[internetMessageId].aiDetails !== undefined) {
                //   await this.dbManager.deleteMail(mailToDelete.id);
                // }

                // WS5b — Suppression copie-exacte du Record (slot), via le delta MailDeletionResult.
                //   Le delta est calculé AVANT le retrait du slot (état Record courant).
                //   (WS5c : payload mail-deleted → uniqueMailvistaId + retrait conditionnel SmartLabel/CustomView.)
                //   Identique que le mail ait été supprimé maintenant ou qu'il ait déjà disparu (404) :
                //   dans les deux cas la copie doit quitter le Record.
                const deletionResult = this._computeDeletionResult(
                    mailToDelete,
                    false,
                );

                // WS5c-2 : retirer la copie du Record AVANT l'émission, pour que le handler voie
                //   l'état post-suppression (copies survivantes) — retrait SmartLabel/CustomView conditionnel.
                this.applyDeletionResult(deletionResult);

                // WS5c (B) : payload copie-aware = la copie supprimée (slot-exact, cf. WS5b).
                this.eventBus.emit("mail-deleted", {
                    uniqueMailvistaId:
                        deletionResult.deletedCopy.uniqueMailvistaId,
                });

                // CO2 Management — uniquement si MailVista a réellement effectué la suppression
                //   (AB#1764 : sur 404 le mail était déjà supprimé ailleurs → pas de gain à comptabiliser).
                if (!alreadyGone) {
                    this.CO2Manager.updateCO2OnDeleteMail();
                }

                this.eventBus.emit("navigation-asking-for-update");
            }

            // Case move to deleted items
            else {
                logger.info(
                    [LOGS.MailManager],
                    `Moving Mail to deleted items in Graph`,
                );

                // WS5c (B) : la copie quitte son dossier d'origine (Inbox/Sent). L'uid de la copie supprimée
                // est exactement le paramètre d'entrée (mailToDelete = la copie ciblée par ce uid).
                const deletedCopyUid = uniqueMailvistaId;

                // Moving real mail
                // AB#1764 — suppression idempotente : si le mail a déjà été supprimé côté messagerie
                //   (Outlook / hors MailVista), le move renvoie 404. On traite ce cas comme un no-op
                //   réussi : la copie n'existe plus à la source → on RETIRE le slot (pas de re-key).
                let movedMailId: string | null = null;
                let alreadyGone = false;
                try {
                    movedMailId = await this.dataManager.moveMail(
                        mailToDelete.id,
                        MailFolder.Deleteditems,
                    );
                } catch (error) {
                    if (!isNotFoundError(error)) throw error;
                    // On interprète le 404 comme « la copie SOURCE a disparu » (déjà supprimée côté messagerie).
                    // Théoriquement Graph pourrait aussi renvoyer 404 pour une destination introuvable, mais
                    // `deleteditems` est un well-known folder stable → risque négligeable.
                    logger.warn(
                        [LOGS.MailManager],
                        `deleteMail - copie déjà supprimée côté messagerie (404), suppression idempotente : ${uniqueMailvistaId}`,
                    );
                    alreadyGone = true;
                }

                // Delete mail in DB only if parentFolderId is Inbox folder
                if (
                    mailToDelete.parentFolderId ===
                    this.mailRecord.inboxFolderId
                ) {
                    // Le backend retire en cascade les todos dashboard rattachés à ce mail lors de
                    // deleteAnalyzedMailsByInternetMessageIds (keyé internetMessageId). On mémorise
                    // AVANT la suppression si ce mail alimentait le dashboard (todos acceptés).
                    const hadDashboardTodos =
                        mailToDelete.aiDetails?.toDo?.some(
                            (todo) => todo.isUserAccepted,
                        ) ?? false;

                    // Updating DB
                    try {
                        // [WS-C / AB#1576] migré Core → Api-Backend : suppression pilotée par internetMessageId
                        await this.apiManager.deleteAnalyzedMailsByInternetMessageIds(
                            [internetMessageId],
                        );
                    } catch {
                        logger.warn(
                            [LOGS.MailManager],
                            `mailId non présent en DB, cas normal s'il n'a pas été analysé : ${internetMessageId}`,
                        );
                    }

                    // deleteAnalyzedMails ne renvoie qu'un compteur et n'émet aucun event todos.
                    // Si ce mail alimentait le dashboard (todos supprimés en cascade côté backend),
                    // on rafraîchit la liste et on émet 'user-todos-updated' pour resynchroniser le
                    // dashboard — cohérent avec addTodoItemToDashboard / setTodoItemState / removeTodoFromDashboard.
                    if (hadDashboardTodos) {
                        try {
                            const userTodos =
                                await this.userTodos.getUserTodos(true);
                            this.eventBus.emit(
                                "user-todos-updated",
                                userTodos.filter((todo) => !todo.done),
                            );
                        } catch (todoError) {
                            logger.warn(
                                [LOGS.MailManager],
                                `deleteMail - échec du refresh des todos après suppression de ${internetMessageId}: ${todoError}`,
                            );
                        }
                    }
                }

                // Updating Record
                if (alreadyGone) {
                    // AB#1764 — la copie n'existe plus à la source → retrait du slot (pas de re-key),
                    //   comme une suppression réussie. Le delta est calculé AVANT le retrait.
                    const deletionResult = this._computeDeletionResult(
                        mailToDelete,
                        false,
                    );
                    this.applyDeletionResult(deletionResult);
                } else {
                    // WS5d : re-key copie-précis du slot d'origine (parentFolderId du uid).
                    this.mailSync._updateRecordWithoutAnalysis(
                        movedMailId!,
                        internetMessageId,
                        parentFolderId,
                    );
                }

                // Emit event mail-deleted
                this.eventBus.emit("mail-deleted", {
                    uniqueMailvistaId: deletedCopyUid,
                });
                this.eventBus.emit("navigation-asking-for-update");
            }

            return true;
        } catch (error) {
            logger.error(
                [LOGS.MailManager],
                `Error deleting mail ${internetMessageId}: ${error}`,
            );
            return false;
        }
    }

    public async executeBulkAction(
        action: BulkMailAction,
        uniqueMailvistaIds: string[],
    ): Promise<BulkActionResult> {
        logger.info([LOGS.MailManager], `executeBulkAction(${action})`);

        if (uniqueMailvistaIds.length === 0) {
            return { success: [], failed: [] };
        }

        const BATCH_SIZE = 10; // Nombre d'emails par lot
        const BATCH_DELAY_MS = 1000; // Délai entre les lots (1 seconde)

        const result: BulkActionResult = { success: [], failed: [] };

        // WS5d : parse les uid → copies EXACTES présentes dans le Record (slot-precise).
        const copies = uniqueMailvistaIds
            .map((uid) => parseUniqueMailvistaId(uid))
            .map((t) =>
                this.mailSync._getCopy(t.internetMessageId, t.parentFolderId),
            )
            .filter((m): m is Mail => m !== undefined);

        // Fonction helper pour créer un délai
        const delay = (ms: number) =>
            new Promise((resolve) => setTimeout(resolve, ms));

        try {
            // Chemins message-level (read/processed/focus) : internetMessageIds dédupliqués (copie→message).
            const mailsRecordByInternetMessageId = [
                ...new Set(copies.map((c) => c.internetMessageId)),
            ];

            // Fast-path DELETE via Graph $batch (copie-précis : on supprime les copies exactes ciblées)
            if (action === BulkMailAction.DELETE) {
                logger.info(
                    [LOGS.MailManager],
                    `BulkMailAction ${action} via $batch (${copies.length} copies)`,
                );
                const batchResult = await this._batchDeleteMails(copies);
                result.success.push(...batchResult.success);
                result.failed.push(...batchResult.failed);
                logger.info(
                    [LOGS.MailManager],
                    `Bulk action ${action} completed: ${result.success.length} success, ${result.failed.length} failed`,
                );
                return result;
            }

            // Fast-path MARK_AS_READ / MARK_AS_UNREAD via Graph $batch (évite les 429 en parallèle)
            if (
                action === BulkMailAction.MARK_AS_READ ||
                action === BulkMailAction.MARK_AS_UNREAD
            ) {
                const isRead = action === BulkMailAction.MARK_AS_READ;
                logger.info(
                    [LOGS.MailManager],
                    `BulkMailAction ${action} via $batch (${copies.length} copies)`,
                );
                const batchResult = await this._batchSetReadStatus(
                    copies,
                    isRead,
                );
                result.success.push(...batchResult.success);
                result.failed.push(...batchResult.failed);
                logger.info(
                    [LOGS.MailManager],
                    `Bulk action ${action} completed: ${result.success.length} success, ${result.failed.length} failed`,
                );
                return result;
            }

            // Calcul du nombre de lots nécessaires
            const totalBatches = Math.ceil(
                mailsRecordByInternetMessageId.length / BATCH_SIZE,
            );
            logger.info(
                [LOGS.MailManager],
                `Processing ${mailsRecordByInternetMessageId.length} emails in ${totalBatches} batch(es) of ${BATCH_SIZE} emails max`,
            );

            // Traitement par lots
            for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
                const startIdx = batchIndex * BATCH_SIZE;
                const endIdx = Math.min(
                    startIdx + BATCH_SIZE,
                    mailsRecordByInternetMessageId.length,
                );
                const batchInternetMessageIds =
                    mailsRecordByInternetMessageId.slice(startIdx, endIdx);

                logger.info(
                    [LOGS.MailManager],
                    `Batch ${batchIndex + 1}/${totalBatches}: Processing ${batchInternetMessageIds.length} emails (${startIdx + 1} to ${endIdx})`,
                );

                let batchPromises: Promise<any>[] = [];

                // Création des promesses selon l'action
                switch (action) {
                    case BulkMailAction.MARK_AS_USER_PROCESSED:
                    case BulkMailAction.MARK_AS_USER_UNPROCESSED: {
                        const isUserProcessed =
                            action === BulkMailAction.MARK_AS_USER_PROCESSED;
                        logger.info(
                            [LOGS.MailManager],
                            `BulkMailAction ${action} to ${isUserProcessed} - Batch ${batchIndex + 1}/${totalBatches}`,
                        );
                        // WS6b : setMailProcessed est uid-public (op canonique) → construire l'uid canonique imid::inboxFolderId.
                        batchPromises = batchInternetMessageIds.map(
                            (internetMessageId) =>
                                this.setMailProcessed(
                                    uniqueMailvistaId({
                                        id: "",
                                        internetMessageId,
                                        parentFolderId:
                                            this.mailRecord.inboxFolderId ?? "",
                                    }),
                                    isUserProcessed,
                                ),
                        );
                        break;
                    }

                    case BulkMailAction.MARK_AS_FOCUS:
                    case BulkMailAction.MARK_AS_UNFOCUS: {
                        const isFocus = action === BulkMailAction.MARK_AS_FOCUS;
                        logger.info(
                            [LOGS.MailManager],
                            `BulkMailAction ${action} to ${isFocus} - Batch ${batchIndex + 1}/${totalBatches}`,
                        );
                        // WS6b : setMailFocus est uid-public (op canonique) → construire l'uid canonique imid::inboxFolderId.
                        batchPromises = batchInternetMessageIds.map(
                            (internetMessageId) =>
                                this.setMailFocus(
                                    uniqueMailvistaId({
                                        id: "",
                                        internetMessageId,
                                        parentFolderId:
                                            this.mailRecord.inboxFolderId ?? "",
                                    }),
                                    isFocus,
                                ),
                        );
                        break;
                    }

                    default:
                        logger.error(
                            [LOGS.MailManager],
                            `Unknown bulk action ${action}`,
                        );
                        continue;
                }

                // Exécution du lot avec gestion des erreurs
                const batchResults = await Promise.allSettled(batchPromises);

                // Traitement des résultats du lot
                batchResults.forEach((batchResult, index) => {
                    const internetMessageId = batchInternetMessageIds[index];
                    // #1728 : FOCUS/PROCESSED sont inbox-only → retour en uniqueMailvistaId canonique
                    // (imid::inboxFolderId), le même uid que celui passé à setMailFocus/setMailProcessed ci-dessus.
                    const uid = uniqueMailvistaId({
                        id: "",
                        internetMessageId,
                        parentFolderId: this.mailRecord.inboxFolderId ?? "",
                    });

                    if (batchResult.status === "fulfilled") {
                        result.success.push(uid);
                    } else {
                        result.failed.push(uid);
                        logger.error(
                            [LOGS.MailManager],
                            `Failed to process mail ${internetMessageId}: ${batchResult.reason}`,
                        );
                    }
                });

                logger.info(
                    [LOGS.MailManager],
                    `Batch ${batchIndex + 1}/${totalBatches} completed: ${result.success.length} success, ${result.failed.length} failed`,
                );

                // Attendre avant le prochain lot (sauf pour le dernier)
                if (batchIndex < totalBatches - 1) {
                    logger.info(
                        [LOGS.MailManager],
                        `Waiting ${BATCH_DELAY_MS / 1000} second(s) before next batch...`,
                    );
                    await delay(BATCH_DELAY_MS);
                }
            }

            logger.info(
                [LOGS.MailManager],
                `Bulk action ${action} completed: ${result.success.length} success, ${result.failed.length} failed`,
            );

            // TODO - Events - Add Object with list of updated mails and action done for Frontend management

            return result;
        } catch (error) {
            logger.error(
                [LOGS.MailManager],
                `Error in bulk action ${action}: ${error}`,
            );
            throw error;
        }
    }

    public async updateMailsWithLinkedEventDetails(
        linkedEventDetailsList: LinkedEventDetails[],
    ): Promise<void> {
        logger.info(
            [LOGS.MailManager],
            `updateMailsWithLinkedEventDetails(${linkedEventDetailsList.length} items)`,
        );

        if (!linkedEventDetailsList || linkedEventDetailsList.length === 0) {
            logger.info([LOGS.MailManager], "No agenda items to process");
            return;
        }

        let updatedMailsCount = 0;

        try {
            // Parcourir tous les mails du record
            Object.keys(this.mailRecord.byInternetMessageId).forEach(
                (internetMessageId) => {
                    const mail =
                        this.mailSync._getCanonicalMail(internetMessageId);

                    // Vérifier si le mail a des détails d'événement lié
                    if (mail?.linkedEventDetails?.id) {
                        // Chercher l'événement correspondant dans la liste d'agenda
                        const correspondingEventDetails =
                            linkedEventDetailsList.find(
                                (item) =>
                                    item.id === mail.linkedEventDetails!.id,
                            );

                        if (correspondingEventDetails) {
                            logger.info(
                                [LOGS.MailManager],
                                `Updating mail ${internetMessageId} with agenda item ${correspondingEventDetails.id}`,
                            );

                            // Mettre à jour les détails de l'événement lié
                            mail.linkedEventDetails = correspondingEventDetails;

                            // Recalculer la taille du composant mail
                            mail.componentSize =
                                this.mailSync._determineMailComponentSize(mail);

                            // Émettre l'événement de mise à jour
                            this.eventBus.emit("mail-updated", mail);
                            updatedMailsCount++;
                        }
                    }
                },
            );

            logger.info(
                [LOGS.MailManager],
                `Updated ${updatedMailsCount} mails with agenda items`,
            );
        } catch (error) {
            logger.error(
                [LOGS.MailManager],
                `Error in updateMailsWithAgendaItems: ${error}`,
            );
            throw error;
        }
    }

    public async addTodoToDashboard(
        internetMessageId: string,
        todoId: string,
    ): Promise<boolean> {
        logger.info(LOGS.MailManager, `addTodoToDashboard(${todoId})`);

        try {
            const mail = this.mailSync._getCanonicalMail(internetMessageId);
            const recordTodoItem = mail?.aiDetails?.toDo?.find(
                (todo) => todo.id === todoId,
            );

            const result: boolean =
                await this.apiManager.addTodoItemToDashboard(todoId);

            // Symétrie avec removeTodoFromDashboard : refléter l'accept dans le record + émettre.
            // Sans ça, `isUserAccepted` reste false côté record ; un rebuild du thread (getTodosFromMailsList)
            // fait alors régresser la tâche à « À accepter » — y compris ses sœurs déjà acceptées.
            if (result && mail && recordTodoItem) {
                recordTodoItem.isUserAccepted = true;
                this.eventBus.emit("mail-updated", mail);
            }

            return result;
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : String(error);
            const errorText = `Error adding todo to dashboard: ${errorMessage}`;
            logger.error(LOGS.MailManager, errorText);
            throw new Error(errorText);
        }
    }

    public async removeTodoFromDashboard(
        internetMessageId: string,
        todoId: string,
    ): Promise<boolean> {
        logger.info(LOGS.MailManager, `removeTodoFromDashboard(${todoId})`);

        try {
            const result: boolean =
                await this.apiManager.removeTodoItemFromDashboard(todoId);

            // Update Record if any and emit Event
            if (result) {
                const mail = this.mailSync._getCanonicalMail(internetMessageId);
                if (mail) {
                    const todo = mail.aiDetails?.toDo?.find(
                        (todo) => todo.id === todoId,
                    );
                    if (todo) {
                        todo.isUserAccepted = false;
                        todo.done = false;
                        this.eventBus.emit("mail-updated", mail);
                    } else {
                        logger.error(
                            [LOGS.MailManager],
                            `removeTodoFromDashboard - Failed update todoId ${todoId} for internetMessageId ${internetMessageId}`,
                        );
                    }
                }
                // else {
                // }
            }

            return result;
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : String(error);
            const errorText = `Error removing todo from dashboard: ${errorMessage}`;
            logger.error(LOGS.MailManager, errorText);
            throw new Error(errorText);
        }
    }

    public async setTodoState(
        internetMessageId: string,
        todoId: string,
        isDone: boolean,
    ): Promise<boolean> {
        logger.info(LOGS.MailManager, `setTodoState(${todoId}) to ${isDone}`);

        try {
            const result: boolean = await this.apiManager.setTodoItemState(
                todoId,
                isDone,
            );

            // Updates record if any
            if (result) {
                const mail = this.mailSync._getCanonicalMail(internetMessageId);
                if (mail) {
                    const todo = mail.aiDetails?.toDo?.find(
                        (todo) => todo.id === todoId,
                    );
                    if (todo) {
                        todo.done = isDone;
                        this.eventBus.emit("mail-updated", mail);
                    } else {
                        logger.error(
                            [LOGS.MailManager],
                            `setTodoState - Failed update todoId ${todoId} for internetMessageId ${internetMessageId}`,
                        );
                    }
                }
                // else {
                // }
            }

            return result;
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : String(error);
            const errorText = `Error setting todo state: ${errorMessage}`;
            logger.error(LOGS.MailManager, errorText);
            throw new Error(errorText);
        }
    }

    /** Reverse lookup par handle Graph volatil (graphId). Renvoie la copie exacte. */
    private _findCopyByGraphId(graphId: string): Mail | undefined {
        return this.mailSync._allCopies().find((m) => m.id === graphId);
    }

    /**
     * WS5b — Calcule le delta de suppression d'une copie à partir de l'état COURANT du Record
     * (à appeler AVANT `applyDeletionResult`, qui retire le slot). En Phase 1 le SDK calcule ce delta
     * après son delete Graph ; en Phase 2 il viendra de l'API (même forme) — cf. 10 §2.
     * `canonicalInboxRemoved` = la copie supprimée était la copie inbox (≤1 inbox par message).
     */
    private _computeDeletionResult(
        deletedMail: Mail,
        dbPurged: boolean,
    ): MailDeletionResult {
        const copies =
            this.mailRecord.byInternetMessageId[deletedMail.internetMessageId];
        const survivingCopies: {
            uniqueMailvistaId: string;
            graphId?: string;
        }[] = [];
        if (copies) {
            for (const [folderId, mail] of copies) {
                if (folderId === deletedMail.parentFolderId) continue; // la copie supprimée elle-même
                survivingCopies.push({
                    uniqueMailvistaId: uniqueMailvistaId(mail),
                    graphId: mail.id,
                });
            }
        }
        return {
            deletedCopy: {
                uniqueMailvistaId: uniqueMailvistaId(deletedMail),
                graphId: deletedMail.id,
            },
            messageStillExists: survivingCopies.length > 0,
            survivingCopies,
            canonicalInboxRemoved:
                deletedMail.parentFolderId === this.mailRecord.inboxFolderId,
            dbPurged,
        };
    }

    public applyDeletionResult(delta: MailDeletionResult): void {
        const { internetMessageId, parentFolderId } = parseUniqueMailvistaId(
            delta.deletedCopy.uniqueMailvistaId,
        );
        this.mailSync._removeCopyFromRecord(internetMessageId, parentFolderId);
    }
}
